'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';

// Helper function to render text with [bold] syntax
const renderTextWithBold = (text: string) => {
  const parts = text.split(/(\[bold][^[]*?\[bold])/g);

  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/\[bold]([^[]*?)\[bold]/);
        if (match) {
          return (
            <strong key={i} className="font-bold text-[40px] leading-[1.2]">
              {match[1]}
            </strong>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

// Helper function to render text with [Bulletpoint] syntax
const renderTextWithBulletpoints = (text: string) => {
  // Check if text contains [Bulletpoint] keyword
  if (text.includes('[Bulletpoint]')) {
    // Split by [Bulletpoint] to separate intro text and bullet items
    const parts = text.split('[Bulletpoint]');
    const introText = parts[0].trim(); // First part is the intro paragraph
    const bulletItems = parts.slice(1).filter((part) => part.trim()); // Rest are bullets

    return (
      <>
        {/* Render intro paragraph if exists */}
        {introText && (
          <p className="text-[18px] text-white leading-[1.5] font-normal font-inter mb-4">
            {introText}
          </p>
        )}

        {/* Render bullet list */}
        {bulletItems.length > 0 && (
          <ul className="list-disc pl-0 mb-4">
            {bulletItems.map((part, index) => {
              const trimmedPart = part.trim();
              return (
                <li
                  key={index}
                  className="text-[18px] leading-[1.5] font-normal font-inter ml-[27px] mb-3 last:mb-0"
                >
                  {trimmedPart}
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  }

  // If no [Bulletpoint] keyword, just return the text as paragraph
  return (
    <p className="text-[18px] text-white leading-[1.5] font-normal font-inter mb-4">
      {text}
    </p>
  );
};

/**
 * Props for `ProofHighlightedBullets`.
 */
export type ProofHighlightedBulletsProps =
  SliceComponentProps<Content.ProofHighlightedBulletsSlice>;

/**
 * Component for "ProofHighlightedBullets" Slices.
 */
const ProofHighlightedBullets: FC<ProofHighlightedBulletsProps> = ({
  slice,
}) => {
  const { intro_title, intro_description, bulleted_list, highlighted_bullets } =
    slice.primary;

  const introRef = useRef<HTMLDivElement>(null);
  const highlightRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-right');
          entry.target.classList.remove('opacity-0', 'translate-x-12');
        }
      });
    }, observerOptions);

    const currentElements: (HTMLDivElement | null)[] = [];

    // Observe intro section
    if (introRef.current) {
      const element = introRef.current;
      currentElements.push(element);
      element.classList.add(
        'opacity-0',
        'translate-x-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      element.style.transitionDelay = '0ms';
      observer.observe(element);
    }

    // Observe highlights with staggered delay
    highlightRefs.current.forEach((highlightEl, index) => {
      if (highlightEl) {
        currentElements.push(highlightEl);
        highlightEl.classList.add(
          'opacity-0',
          'translate-x-12',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        highlightEl.style.transitionDelay = `${(index + 1) * 150}ms`;
        observer.observe(highlightEl);
      }
    });

    return () => {
      currentElements.forEach((element) => {
        if (element) observer.unobserve(element);
      });
    };
  }, [highlighted_bullets]);

  const introDescriptionText = asText(intro_description) || '';
  const bulletedListText = asText(bulleted_list) || '';

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-12 md:py-16 lg:py-24"
      style={{
        background: 'linear-gradient(180deg, #0E1110 0%, #0F423A 100%)',
      }}
    >
      <div className="container mx-auto px-6 lg:px-[200px]">
        <div className="flex flex-col gap-12 md:gap-14 lg:gap-[60px]">
          {/* Intro Section */}
          <div
            ref={introRef}
            className="flex flex-col gap-6 pb-6 border-b-0 lg:border-b-0"
          >
            {intro_title && (
              <div>
                <PrismicRichText
                  field={intro_title}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="text-3xl md:text-[40px] font-medium text-white leading-[1.2] font-inter">
                        {children}
                      </h2>
                    ),
                  }}
                />
              </div>
            )}

            {/* Description with [Bulletpoint] support */}
            <div className="text-white text-[18px] leading-[1.5] font-normal font-inter">
              {intro_description && introDescriptionText && (
                <div>{renderTextWithBulletpoints(introDescriptionText)}</div>
              )}

              {bulleted_list && bulletedListText && (
                <div>{renderTextWithBulletpoints(bulletedListText)}</div>
              )}
            </div>
          </div>

          {/* Highlighted Bullets Section */}
          {highlighted_bullets && highlighted_bullets.length > 0 && (
            <div className="flex flex-col gap-6 max-w-[1040px]">
              {highlighted_bullets.map((bullet, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    highlightRefs.current[index] = el;
                  }}
                  className="rounded-3xl px-6 py-4 lg:px-6 lg:py-4"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0) 100%)',
                  }}
                >
                  <div className="flex gap-2 items-center text-white">
                    {bullet.highlight_label && (
                      <p className="text-2xl lg:text-[40px] font-bold leading-[1.2] font-inter whitespace-pre">
                        {renderTextWithBold(bullet.highlight_label)}
                      </p>
                    )}

                    {bullet.highlight_description && (
                      <div className="text-[18px] font-medium leading-[1.5] font-inter">
                        <PrismicRichText
                          field={bullet.highlight_description}
                          components={{
                            paragraph: ({ children }) => (
                              <p className="whitespace-pre">{children}</p>
                            ),
                            strong: ({ children }) => (
                              <strong className="font-bold">{children}</strong>
                            ),
                            em: ({ children }) => (
                              <em className="italic">{children}</em>
                            ),
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProofHighlightedBullets;
